import { valibotResolver } from '@hookform/resolvers/valibot';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import * as v from 'valibot';
import { useSignUp, useSocialSignIn } from '@/api/auth/queries';
import GithubIcon from '@/assets/socials/github.svg?react';
import GoogleIcon from '@/assets/socials/google.svg?react';
import { PasswordInput } from '@/components/password-input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

const signupSchema = v.object({
  name: v.pipe(
    v.string(),
    v.minLength(2, 'Name must be at least 2 characters'),
    v.maxLength(50, 'Name must be less than 50 characters'),
  ),
  email: v.pipe(v.string(), v.email('Please enter a valid email address')),
  password: v.pipe(
    v.string(),
    v.minLength(8, 'Password must be at least 8 characters'),
    v.regex(/[A-Z]/, 'Password must contain at least one uppercase letter'),
    v.regex(/[a-z]/, 'Password must contain at least one lowercase letter'),
    v.regex(/[0-9]/, 'Password must contain at least one number'),
  ),
  confirmPassword: v.pipe(v.string(), v.minLength(1, 'Please confirm your password')),
});

const signupSchemaWithConfirm = v.pipe(
  signupSchema,
  v.forward(
    v.partialCheck(
      [['password'], ['confirmPassword']],
      (input) => input.password === input.confirmPassword,
      'Passwords do not match',
    ),
    ['confirmPassword'],
  ),
);

type SignupFormData = v.InferInput<typeof signupSchemaWithConfirm>;

export function SignupForm() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ strict: false }) as { redirect: string | undefined };
  const signUpMutation = useSignUp();
  const socialSignInMutation = useSocialSignIn();

  const form = useForm<SignupFormData>({
    resolver: valibotResolver(signupSchemaWithConfirm),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (data: SignupFormData) => {
    return signUpMutation.mutateAsync(data, {
      onSuccess: () => {
        setTimeout(() => {
          navigate({ to: '/verify-email', search: { redirect } });
        }, 0);
      },
    });
  };

  const handleSocialSignUp = (provider: 'github' | 'google') => {
    const callback = `/oauth/success?redirect=${redirect || '/projects'}`;
    socialSignInMutation.mutate({ provider, callbackURL: callback });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h1>Create Account</h1>
        </CardTitle>
        <CardDescription>Enter your information below to create your account</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="John Doe"
                      disabled={form.formState.isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="m@example.com"
                      disabled={form.formState.isSubmitting}
                      autoComplete="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      disabled={form.formState.isSubmitting}
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      disabled={form.formState.isSubmitting}
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>
        </Form>

        <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t py-6">
          <span className="bg-card text-muted-foreground relative z-10 px-2">Or continue with</span>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="outline"
            type="button"
            onClick={() => handleSocialSignUp('github')}
            disabled={form.formState.isSubmitting || socialSignInMutation.isPending}
          >
            <GithubIcon className="mr-2 h-4 w-4" />
            GitHub
          </Button>
          <Button
            variant="outline"
            type="button"
            onClick={() => handleSocialSignUp('google')}
            disabled={form.formState.isSubmitting || socialSignInMutation.isPending}
          >
            <GoogleIcon className="mr-2 h-4 w-4" />
            Google
          </Button>
        </div>

        <div className="mt-4 text-center text-sm">
          Already have an account?{' '}
          <Link to="/sign-in" search={{ redirect }} className="underline">
            Sign in
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
